class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy || !this.activeElement?.isConnected) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const wasActive = this.classList.contains('active');
    this._focusTrapActive = true;
    this.classList.add('animate', 'active');
    if (this._focusTrapEnforcer) {
      document.removeEventListener('focusin', this._focusTrapEnforcer, true);
    }
    this._focusTrapEnforcer = (event) => {
      if (
        this._focusTrapActive &&
        (!this._focusTrapContainer?.isConnected || !this._focusTrapTarget?.isConnected)
      ) {
        this.syncFocusTrap({focus: false});
      }
      if (
        !this._focusTrapActive ||
        !this._focusTrapContainer ||
        this._focusTrapContainer.contains(event.target) ||
        !this._focusTrapTarget
      ) {
        return;
      }
      event.stopPropagation();
      this._focusTrapTarget.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', this._focusTrapEnforcer, true);

    // Keep the trap container and initial focus target in the same complete
    // dialog. Focus only after the active state has painted; focusing before
    // activation lets pointer and keyboard activation leave focus on the opener.
    this.syncFocusTrap({focus: false});
    const currentFocus = document.activeElement;
    const focusIsInsideDialog =
      currentFocus?.isConnected === true && this._focusTrapContainer?.contains(currentFocus) === true;
    if (focusIsInsideDialog && this._focusTrapContainer && currentFocus) {
      // A caller may have focused a connected drawer control before activation
      // (for example, a refresh or keyboard hand-off). Install Dawn's boundary
      // handlers around that live element immediately; the deferred path must
      // not be the only place that creates the trap.
      trapFocus(this._focusTrapContainer, currentFocus);
      if (document.activeElement === currentFocus) {
        // Dawn arms its document-level Tab listener from focusin. Calling
        // focus() on an already-focused node does not emit focusin, so replay
        // the boundary event once after trapFocus has replaced its handlers.
        const FocusEventConstructor = currentFocus.ownerDocument?.defaultView?.FocusEvent;
        const focusinEvent = FocusEventConstructor
          ? new FocusEventConstructor('focusin', {bubbles: true})
          : new Event('focusin', {bubbles: true});
        currentFocus.dispatchEvent(focusinEvent);
      }
    }
    if (!wasActive || !focusIsInsideDialog) this.scheduleFocusTrap();

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.cancelDeferredCartFocus();
    this.cancelScheduledFocusTrap();
    this.classList.remove('active');
    this._focusTrapActive = false;
    if (this._focusTrapEnforcer) {
      document.removeEventListener('focusin', this._focusTrapEnforcer, true);
    }
    const restoreTarget =
      this.activeElement?.isConnected === true
        ? this.activeElement
        : document.getElementById('cart-icon-bubble');
    removeTrapFocus(restoreTarget);
    this._focusTrapContainer = null;
    this._focusTrapTarget = null;
    document.body.classList.remove('overflow-hidden');
  }

  registerDeferredCartFocus(timer) {
    if (!this._cartFocusTimers) this._cartFocusTimers = new Set();
    this._cartFocusTimers.add(timer);
  }

  cancelDeferredCartFocus() {
    const view = this.ownerDocument?.defaultView || globalThis;
    this._cartFocusTimers?.forEach((timer) => view.clearTimeout(timer));
    this._cartFocusTimers?.clear();
  }

  scheduleFocusTrap() {
    this.cancelScheduledFocusTrap();
    const view = this.ownerDocument?.defaultView || globalThis;
    const focusAfterPaint = () => {
      this._focusTrapFrame = null;
      this._focusTrapFrameType = null;
      if (!this.isConnected || !this.classList.contains('active') || !this._focusTrapActive) return;

      this.syncFocusTrap({focus: false});
      const dialog = this._focusTrapContainer;
      const focusElement = this._focusTrapTarget;
      if (!dialog?.isConnected || !focusElement?.isConnected || !dialog.contains(focusElement)) {
        this.scheduleFocusTrapRetry();
        return;
      }

      // Pointer and keyboard activation can leave focus on the opener until
      // after the active drawer has painted. Never steal focus from a user who
      // has already moved inside the dialog, but recover the close control when
      // focus is still outside.
      if (!dialog.contains(document.activeElement)) {
        trapFocus(dialog, focusElement);
      }

      // A second frame covers browsers that paint the drawer and dispatch the
      // activation event in separate tasks. It is guarded by the same active
      // and connected checks and only runs while focus remains outside.
      if (!dialog.contains(document.activeElement)) this.scheduleFocusTrapRetry();
    };

    if (typeof view.requestAnimationFrame === 'function') {
      this._focusTrapFrameType = 'animation-frame';
      this._focusTrapFrame = view.requestAnimationFrame(focusAfterPaint);
    } else {
      this._focusTrapFrameType = 'timeout';
      this._focusTrapFrame = view.setTimeout(focusAfterPaint, 0);
    }
  }

  scheduleFocusTrapRetry() {
    if (this._focusTrapRetryFrame != null) return;
    const view = this.ownerDocument?.defaultView || globalThis;
    const retryFocus = () => {
      this._focusTrapRetryFrame = null;
      this._focusTrapRetryFrameType = null;
      if (!this.isConnected || !this.classList.contains('active') || !this._focusTrapActive) return;

      this.syncFocusTrap({focus: false});
      const dialog = this._focusTrapContainer;
      const focusElement = this._focusTrapTarget;
      if (!dialog?.isConnected || !focusElement?.isConnected || !dialog.contains(focusElement)) return;
      if (!dialog.contains(document.activeElement)) trapFocus(dialog, focusElement);
    };

    if (typeof view.requestAnimationFrame === 'function') {
      this._focusTrapRetryFrameType = 'animation-frame';
      this._focusTrapRetryFrame = view.requestAnimationFrame(retryFocus);
    } else {
      this._focusTrapRetryFrameType = 'timeout';
      this._focusTrapRetryFrame = view.setTimeout(retryFocus, 0);
    }
  }

  cancelScheduledFocusTrap() {
    const view = this.ownerDocument?.defaultView || globalThis;
    const cancel = (frame, frameType) => {
      if (frame == null) return;
      if (frameType === 'animation-frame' && typeof view.cancelAnimationFrame === 'function') {
        view.cancelAnimationFrame(frame);
      } else {
        view.clearTimeout(frame);
      }
    };

    cancel(this._focusTrapFrame, this._focusTrapFrameType);
    cancel(this._focusTrapRetryFrame, this._focusTrapRetryFrameType);
    this._focusTrapFrame = null;
    this._focusTrapFrameType = null;
    this._focusTrapRetryFrame = null;
    this._focusTrapRetryFrameType = null;
  }

  syncFocusTrap({focus = true} = {}) {
    const dialog = this.querySelector('#CartDrawer');
    const focusElement = dialog?.querySelector(
      '.drawer__close, .drawer__inner-empty a[href], .drawer__inner a[href], .drawer__inner button:enabled, .drawer__inner input:not([type="hidden"]):enabled, .drawer__inner select:enabled, .drawer__inner textarea:enabled, .drawer__inner [tabindex]:not([tabindex^="-"])'
    );

    this._focusTrapContainer = dialog;
    this._focusTrapTarget = focusElement;
    if (focus && this._focusTrapActive && dialog && focusElement) {
      trapFocus(dialog, focusElement);
    }
    return focusElement;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasActive = this.classList.contains('active') && this._focusTrapActive;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncFocusTrap({focus: false});

    setTimeout(() => {
      if (!this.isConnected) return;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (wasActive) {
        if (!this.classList.contains('active') || !this._focusTrapActive) return;
        this.classList.add('animate', 'active');
        this._focusTrapActive = true;
        const refreshedTarget = this.syncFocusTrap({focus: false});
        if (this._focusTrapContainer && refreshedTarget) {
          // Recreate Dawn's first/last handlers around the replacement nodes.
          // A custom mutation may queue a more specific target (discount or
          // upsell) immediately after this task, so that target can win.
          trapFocus(this._focusTrapContainer, refreshedTarget);
        }
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element || document.getElementById('cart-icon-bubble');
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
